/**
 * `Icon`: every small picture a screen draws, by name, at a size, in a tone (DESIGN_SYSTEM.md §5.1). Five kinds:
 *
 * - a VECTOR glyph (`library/icons/vectors.ts`): one path, filled with the tone;
 * - a COMPONENT glyph (`library/icons/glyphs.tsx`): a whole drawing, in the tone (QD10);
 * - a SPRITE cell (`library/icons/spriteCells.ts`): a cell of v1's sheet, drawn from v2's one generated sheet as
 *   the sheet clipped to the cell (QD6, QD9); it has its own colours and ignores the tone;
 * - an IMAGE (`image="<key>"`): a fixed picture the shell supplies through `IconAssetsProvider`;
 * - a CDN icon (`cdn="<name>"`, `library/icons/cdnIcons.ts`): one v1 draws from the CDN (S3), its own prop because
 *   its names are v1's file names, two of which a sprite cell also uses for a different drawing. An SVG is drawn by
 *   react-native-svg's `SvgUri` on native, where `Image` cannot draw one; an `Image` otherwise. It keeps its colours.
 *
 * `size` is the height (12, 16, 20, 24, 32 are the scale); a sprite cell or image that is not square keeps its
 * aspect. Decorative unless `label` is given, when a screen reader reads it as an image. The scale, the tones and
 * the kinds are shown and copied in the gallery's Icons section.
 */
import React from "react";
import { Image, Platform, View } from "react-native";
import Svg, { Path, SvgUri } from "react-native-svg";

import { useIconAssets } from "@rista/ui/library/icons/IconAssets";
import { CDN_ICONS, CDN_ICON_BASE, type CdnIconName } from "@rista/ui/library/icons/cdnIcons";
import { COMPONENT_ICONS, type ComponentIconName } from "@rista/ui/library/icons/glyphs";
import { SPRITE_CELLS, SPRITE_SHEET_SIZE, type SpriteCellName } from "@rista/ui/library/icons/spriteCells";
import { VECTOR_ICONS, type VectorGlyph, type VectorIconName } from "@rista/ui/library/icons/vectors";
import { useTheme } from "@rista/ui/library/theme/ThemeProvider";
import type { Theme } from "@rista/ui/library/theme/theme";

export type IconName = VectorIconName | ComponentIconName | SpriteCellName;

/** the icon scale */
export const ICON_SIZES = [12, 16, 20, 24, 32] as const;

/** a text or status role */
export type IconTone = "primary" | "secondary" | "disabled" | "inverse" | "link" | "success" | "warning" | "danger" | "info";

const toneColour = (t: Theme, tone: IconTone): string =>
  tone in t.color.text ? t.color.text[tone as keyof Theme["color"]["text"]] : t.color.status[tone as "success"].fg;

export interface IconProps {
  /** a vector, component or sprite glyph; omitted when `image` is given */
  name?: IconName;
  /** a picture the shell supplied to `IconAssetsProvider`, by key */
  image?: string;
  /** an icon v1 draws from the CDN, by its file name (`library/icons/cdnIcons.ts`) */
  cdn?: CdnIconName;
  /** its height; 20 by default */
  size?: number;
  /** a text or status role; `primary` by default. Sprites and images keep their own colours */
  tone?: IconTone;
  /** a colour over the tone, for a domain mark whose colour is data (a veg mark, a merchant's) */
  color?: string;
  /** read out as an image; without it the icon is decoration */
  label?: string;
  testID?: string;
}

export function Icon({ name, image, cdn, size = 20, tone = "primary", color, label, testID }: IconProps) {
  const theme = useTheme();
  const assets = useIconAssets();
  const ink = color ?? toneColour(theme, tone);
  const a11y = label
    ? { accessibilityRole: "image" as const, accessibilityLabel: label }
    : // `aria-hidden`: react-native-web 0.21 no longer translates `accessibilityElementsHidden`; RN 0.87 reads both
      { "aria-hidden": true, accessibilityElementsHidden: true, importantForAccessibility: "no-hide-descendants" as const };

  let body: React.ReactNode = null;
  let box = { width: size, height: size };
  if (cdn !== undefined) {
    const uri = CDN_ICON_BASE + CDN_ICONS[cdn];
    // PLATFORM: React Native's Image cannot draw an SVG; react-native-svg's SvgUri fetches and draws it there
    body =
      Platform.OS !== "web" && uri.endsWith(".svg") ? (
        <SvgUri uri={uri} width={size} height={size} />
      ) : (
        <Image source={{ uri }} style={box} resizeMode="contain" />
      );
  } else if (image !== undefined) {
    const source = assets.images?.[image];
    body = source ? <Image source={source} style={box} resizeMode="contain" /> : null;
  } else if (name && name in VECTOR_ICONS) {
    const glyph: VectorGlyph = VECTOR_ICONS[name as VectorIconName];
    const [, , w = 24, h = 24] = (glyph.viewBox ?? "0 0 24 24").split(" ").map(Number);
    box = { width: (size * w) / h, height: size };
    body = (
      <Svg width={box.width} height={box.height} viewBox={glyph.viewBox ?? "0 0 24 24"}>
        <Path d={glyph.d} fill={ink} />
      </Svg>
    );
  } else if (name && name in COMPONENT_ICONS) {
    const Glyph = COMPONENT_ICONS[name as ComponentIconName];
    // a component sizes itself; the wrapper only carries the test id and the reading
    return (
      <View testID={testID} {...a11y}>
        <Glyph size={size} color={ink} />
      </View>
    );
  } else if (name && name in SPRITE_CELLS) {
    const cell = SPRITE_CELLS[name as SpriteCellName];
    const scale = size / cell.height;
    box = { width: cell.width * scale, height: size };
    body = assets.sprite ? (
      <Image
        source={assets.sprite}
        style={{
          position: "absolute",
          // whole pixels: at a scale between them the sheet lands mid-pixel and the next cell's edge shows
          width: Math.round(SPRITE_SHEET_SIZE.width * scale),
          height: Math.round(SPRITE_SHEET_SIZE.height * scale),
          // rtl-exempt: an offset into a picture, not an edge of the layout; mirrored it would show the wrong cell
          left: -Math.round(cell.x * scale),
          top: -Math.round(cell.y * scale),
        }}
      />
    ) : null;
  }
  return (
    <View testID={testID} style={[box, { overflow: "hidden" }]} {...a11y}>
      {body}
    </View>
  );
}

export default Icon;
